import React, { useState } from 'react';
import {
    TFreelancerData,
    TRoleDetails,
    TRoles,
    TServicesStructure
} from '../../types';
import { AddNewRoleRow } from './AddNewRoleRow';
import ExtraSkillsSelect from './FreelancerDetailsSelectExtraSkills';
import { freelancerRolesDetails, serviceTypes } from '../../consts';
import { getErrorMessages, reduceDuplicatedKeyValPairs } from '../../utils';
import { executeHttpRequest } from '../../epics/api';


export type TFreelancerDetailsRolesAndSkills = {
    editionEnabled: boolean;
    freelancerData: TFreelancerData;
    fetchListOf: (role: string, url: string) => Promise<any>;
    roles: TRoles;
    servicesStructure: TServicesStructure;
};

export default function FreelancerDetailsRolesAndSkills({
    editionEnabled,
    freelancerData,
    fetchListOf,
    roles,
    servicesStructure,
}: TFreelancerDetailsRolesAndSkills) {

    const [error, setError] = useState(null);
    const [fields, setFields] = useState<TRoleDetails>();
    const [editingID, setEditingID] = useState(0);

    const serviceFilterName = freelancerRolesDetails[freelancerData.service_key];
    const roleName = freelancerData.service_key === serviceTypes.DRIVER ? 'vehicle_type' : 'type';
    const rolesField = servicesStructure[freelancerData.service_key].role_filter_name || 'role';
    const serviceRoleNumber = Object.entries(servicesStructure[freelancerData.service_key][rolesField])
        .filter(([key ]) => key !== '').length;

    const getServiceRolesList = () => {
        const service = servicesStructure[freelancerData.service_key];
        const rolesObject = service[rolesField] || {};

        const serviceRolesList = Object.entries(rolesObject)
            .map(([key, value]) => {
                if (key !== '') {
                    return { value: key, label: value.label };
                }
                return null;
            });
        return [...new Set(serviceRolesList)];
    };

    const handleSaveButtonClick = (role: TRoleDetails) => {
        const data = reduceDuplicatedKeyValPairs(fields, role);

        if (Object.keys(data).length) {
            executeHttpRequest(
                `freelancers/${freelancerData.id}/${serviceFilterName}/${role.id}/`,
                'patch',
                data,
            )
                .then(() => {
                    fetchListOf(
                        'roles',
                        `freelancers/${freelancerData.id}/${serviceFilterName}/`,
                    ).then(() => {
                        setEditingID(null);
                        setFields(null);
                        setError(null);
                    });
                })
                .catch(err => {
                    setError(getErrorMessages(err));
                });
        } else {
            setEditingID(null);
            setError(null);
        }
    };

    const handleEditButtonClick = (role: TRoleDetails) => {
        setEditingID(role.id);
        const filteredFields = {
            [roleName]: role[roleName],
            active: role.active,
            ...(freelancerData.service_key === serviceTypes.DRIVER && { own_vehicle: role.own_vehicle })
        };
        setFields(filteredFields);
    };

    const handleCheckboxFieldChange = (event: { target: HTMLInputElement }, fieldName: string) => {
        setFields({
            ...(fields as TRoleDetails),
            [fieldName]: event.target.checked,
        });
    };

    const renderActionButtons = (role: TRoleDetails) => {
        if (editionEnabled) {
            if (editingID === role.id) {
                return (
                    <td className="actions center">
                        <button
                            onClick={() => handleSaveButtonClick(role)}
                            type="button"
                        >
                            <i className="icon icon icon-check-12" />
                        </button>
                        <button
                            onClick={() => {
                                setEditingID(null);
                                setError(null);
                            }}
                            type="button"
                        >
                            <i className="icon icon-close2" />
                        </button>
                    </td>
                );
            }

            return (
                <td className="actions center">
                    <button
                        disabled={editingID === role.id}
                        onClick={() => handleEditButtonClick(role)}
                        type="button"
                    >
                        <i className="icon icon-pencil-3" />
                    </button>
                </td>
            );
        }

        return null;
    };

    const renderRows = () => {
        const freelancerRoles = roles.content;
        const serviceRolesList = getServiceRolesList();

        return freelancerRoles.map((role: any) => {
            const roleType = serviceRolesList.filter(
                iRole => typeof iRole?.value === 'string'
                        ? iRole?.value === role[roleName].toString()
                        : +iRole?.value === role[roleName]
            )[0];

            return (
                <tr key={roleType?.value} data-testid={roleType?.value}>
                    <td>{roleType?.label}</td>
                    <td>
                        <ExtraSkillsSelect
                            serviceKey={freelancerData.service_key}
                            selectedRole={role}
                            fields={fields}
                            servicesStructure={servicesStructure}
                            disabled={editingID !== role.id}
                            update={setFields}
                            editingID={editingID}
                        />
                    </td>
                    <td data-testid='active'>
                        {editingID === role.id ? (
                            <input
                                name='active'
                                className="checkbox-purple"
                                type="checkbox"
                                checked={fields.active}
                                onChange={event => {
                                    handleCheckboxFieldChange(event, 'active');
                                }}
                            />
                        ) : (
                            <i className={`icon ${role.active ? 'icon-check-12' : 'icon-close2'}`}/>
                        )}
                    </td>
                    {freelancerData.service_key === serviceTypes.DRIVER && (
                        <td data-testid='own_vehicle'>
                            {editingID === role.id ? (
                                <input
                                    name='own_vehicle'
                                    className="checkbox-purple"
                                    type="checkbox"
                                    checked={fields.own_vehicle}
                                    onChange={event => {
                                        handleCheckboxFieldChange(event, 'own_vehicle');
                                    }}
                                />
                            ) : (
                                <i className={`icon ${role.own_vehicle ? 'icon-check-12' : 'icon-close2'}`}/>
                            )}
                        </td>
                    )}
                    {renderActionButtons(role)}
                </tr>
            );
        });
    };

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">
                    {(freelancerData.service_key === serviceTypes.DRIVER
                        ? 'Vehicles'
                        : 'Roles details'
                    )}
                </div>
            </div>
            <div className="freelancer-details-section__content">
                {!!error &&
                    (<div className="freelancer-details-section__errors">
                        <p data-testid="errorMsg">
                            {error}
                        </p>
                    </div>)
                }
                <table className="table-default">
                    <tbody>
                    <tr>
                        <td className="table-default__cell--text-blue">
                            {(freelancerData.service_key === serviceTypes.DRIVER
                                ? 'Vehicles'
                                : 'Roles'
                            )}
                        </td>
                        <td className="table-default__cell--text-blue">
                            {(freelancerData.service_key === serviceTypes.DRIVER
                                ? 'Additional licenses'
                                : 'Extra skills'
                            )}
                        </td>
                        <td className="table-default__cell--text-blue table-default__cell--active">
                            Active
                        </td>
                        {freelancerData.service_key === serviceTypes.DRIVER && (
                            <td className="table-default__cell--text-blue table-default__cell--owns-vehicle">
                                Owns vehicle
                            </td>
                        )}
                        {!!editionEnabled && (
                            <td className="table-default__cell--text-blue center table-default__cell--action">
                                Action
                            </td>
                        )}
                    </tr>
                    {renderRows()}
                    {(freelancerData.service_key && serviceRoleNumber !== roles.content.length) && (
                        <AddNewRoleRow
                            fetchListOf={fetchListOf}
                            freelancerData={freelancerData}
                            roles={roles.content}
                            roleName={roleName}
                            servicesStructure={servicesStructure}
                            serviceFilterName={serviceFilterName}
                            setEditingID={setEditingID}
                            setError={setError}
                        />
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
